import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, CheckCircle2, List, PieChart, LogIn, Plus, X, Trash2, Image as ImageIcon, Camera, Sparkles, Pin } from 'lucide-react';
import AuthCTA from './AuthCTA';
import EmptyState from './EmptyState';
import LiveSyncIndicator from './LiveSyncIndicator';
import InteractionTip from './InteractionTip';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Link } from 'react-router-dom';
import { cn } from '../utils';
import { api } from '../api';
import usePolling from '../hooks/usePolling';
import useRateLimit from '../hooks/useRateLimit';
import ConfirmModal from './ConfirmModal';

import { useRoomTheme } from '../hooks/useRoomTheme';

const LivePolls = ({ sessionState = 'live', sessionId, communityId, isModerator = false, roomData = null }) => {
  const { user } = useAuth();
  const { theme } = useRoomTheme(roomData?.settings?.room_type || 'Mixed');
  const { addToast } = useToast();
  const [selectedOptions, setSelectedOptions] = useState({});
  const [polls, setPolls] = useState([]);
  const { isSlowMode, timer, startCooldown } = useRateLimit(`polls-${sessionId}`, 30);
  const [confirmConfig, setConfirmConfig] = useState({ isOpen: false });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const createOption = (color) => ({ id: Math.random().toString(36).substr(2, 9), text: '', color, image: null });

  const handlePollInputChange = (field, value) => {
    setNewPoll(prev => ({ ...prev, [field]: value }));
  };

  const handleOptionTextChange = (index, value) => {
    setNewPoll(prev => ({
      ...prev,
      options: prev.options.map((option, idx) => idx === index ? { ...option, text: value } : option)
    }));
  };
  const [newPoll, setNewPoll] = useState({
    question: '',
    poll_type: 'single',
    options: [
      createOption('bg-brand-indigo'),
      createOption('bg-purple-500')
    ]
  });

  const handleOptionImageChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPoll(prev => ({
          ...prev,
          options: prev.options.map((option, idx) => idx === index ? { ...option, image: reader.result } : option)
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const fetchPolls = async () => {
    if (!sessionId) return;
    try {
      const endpoint = user ? `/polls?room_id=${sessionId}` : `/public/polls?room_id=${sessionId}`;
      const data = await api.get(endpoint);
      const pollsData = Array.isArray(data) ? data : [];
      
      setPolls(prev => {
        const existingMap = new Map(prev.map(p => [p.id, p]));
        pollsData.forEach(p => existingMap.set(p.id, p));
        return Array.from(existingMap.values());
      });
      
      // Map user's votes to selectedOptions state
      const initialVotes = {};
      pollsData.forEach(poll => {
        if (poll.votes && poll.votes.length > 0) {
          if (poll.poll_type === 'multiple') {
            initialVotes[poll.id] = poll.votes.map(v => v.option_id);
          } else {
            initialVotes[poll.id] = poll.votes[0].option_id;
          }
        }
      });
      setSelectedOptions(prev => ({ ...prev, ...initialVotes }));
      
      setLastSync(new Date());
      setIsInitialLoading(false);
    } catch (err) {
      console.error('Failed to load polls', err);
      throw err;
    }
  };

  const { isSyncing } = usePolling(fetchPolls, 5000, [sessionId]);

  const handleVote = async (pollId, optionId, pollType = 'single') => {
    const currentSelected = selectedOptions[pollId];
    
    if (sessionState !== 'live' || !user || isSlowMode) {
      if (isSlowMode) {
        setError(`Slow mode active. Please wait ${timer}s.`);
        setTimeout(() => setError(''), 3000);
      }
      return;
    }

    // For single choice, prevent voting if already voted
    if (pollType === 'single' && currentSelected) return;
    
    // For multiple choice, prevent voting if already voted for this specific option
    if (pollType === 'multiple' && Array.isArray(currentSelected) && currentSelected.includes(optionId)) return;

    startCooldown();

    try {
      const updatedPoll = await api.post(`/polls/${pollId}/vote`, { option_id: optionId });
      
      setSelectedOptions(prev => {
        if (pollType === 'multiple') {
          const prevArr = Array.isArray(prev[pollId]) ? prev[pollId] : [];
          return { ...prev, [pollId]: [...prevArr, optionId] };
        }
        return { ...prev, [pollId]: optionId };
      });
      
      // Update the specific poll in the list to reflect new vote counts immediately
      setPolls(prev => prev.map(p => p.id === pollId ? updatedPoll : p));
    } catch (err) {
      console.error('Vote failed', err);
      setError(err?.message || 'Unable to submit vote. Please try again.');
    }
  };

  const handleCreatePoll = async () => {
    if (!newPoll.question.trim() || newPoll.options.some(o => !o.text.trim()) || isSlowMode) {
      if (isSlowMode) {
        setError(`Slow mode active. Please wait ${timer}s.`);
        setTimeout(() => setError(''), 3000);
      } else {
        setError('Please fill in the question and all options.');
      }
      return;
    }

    startCooldown();

    try {
      const created = await api.post('/polls', {
        question: newPoll.question,
        poll_type: newPoll.poll_type,
        options: newPoll.options,
        room_id: sessionId,
        community_id: communityId,
      });
      setPolls(prev => [created, ...prev]);
      setIsCreating(false);
      setNewPoll({
        question: '',
        poll_type: 'single',
        options: [
          createOption('bg-brand-indigo'),
          createOption('bg-purple-500')
        ]
      });
      setError('');
    } catch (err) {
      console.error('Failed to create poll', err);
      setError('Failed to create poll. Please try again.');
    }
  };

  const handleEndPoll = async (pollId) => {
    try {
      await api.put(`/polls/${pollId}`, { status: 'ended' });
      await fetchPolls();
    } catch (err) {
      console.error('Failed to end poll', err);
    }
  };

  const handleDeletePoll = async (pollId) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Delete Poll?',
      description: 'Are you sure you want to delete this poll? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await api.delete(`/polls/${pollId}`);
          setPolls(prev => prev.filter(p => p.id !== pollId));
          setConfirmConfig({ isOpen: false });
        } catch (err) {
          console.error('Failed to delete poll', err);
        }
      }
    });
  };

  const togglePin = async (pollId, currentHighlighted) => {
    try {
      const updated = await api.put(`/polls/${pollId}`, { highlighted: !currentHighlighted });
      setPolls((prev) => Array.isArray(prev) ? prev.map((p) => p.id === updated.id ? updated : p) : []);
    } catch (err) {
      console.error('Failed to toggle pin', err);
    }
  };

  const addOption = () => {
    if (newPoll.options.length >= 5) return;
    const colors = ['bg-brand-indigo', 'bg-purple-500', 'bg-blue-500', 'bg-green-500', 'bg-pink-500'];
    setNewPoll(prev => ({
      ...prev,
      options: [...prev.options, { id: Math.random().toString(36).substr(2, 9), text: '', color: colors[prev.options.length], image: null }]
    }));
  };

  const removeOption = (index) => {
    if (newPoll.options.length <= 2) return;
    setNewPoll(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const getVariant = () => {
    const t = theme.type?.toLowerCase();
    if (t === 'mixed') return 'orange';
    if (t === 'poll') return 'indigo';
    if (t === 'brainstorm') return 'green';
    return 'purple';
  };

  const variant = getVariant();

  const syncLabel = lastSync ? 'Live Sync active' : 'Connecting...';

  if (isCreating && isModerator) {
    return (
      <div className={cn("flex flex-col h-full bg-white rounded-2xl p-4 border shadow-xl", theme.border, theme.shadow)}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 uppercase tracking-widest text-xs">Create Live Poll</h3>
          <button onClick={() => setIsCreating(false)} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="text-xxs font-bold text-gray-400 uppercase tracking-widest mb-1.5 block">Question</label>
            <input 
              type="text"
              value={newPoll.question}
              onChange={(e) => handlePollInputChange('question', e.target.value)}
              placeholder="What's on your mind?"
              className={cn(
                "w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 outline-none text-sm font-medium transition-all",
                theme.ring,
                `focus:${theme.border}`
              )}
            />
          </div>

          <div>
            <label className="text-xxs font-bold text-gray-400 uppercase tracking-widest mb-1.5 block">Poll Type</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'single', label: 'Single', icon: List },
                { id: 'multiple', label: 'Multiple', icon: BarChart3 },
                { id: 'ranked', label: 'Ranked', icon: PieChart },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setNewPoll(prev => ({ ...prev, poll_type: t.id }))}
                  className={cn(
                    "flex flex-col items-center gap-1 p-2 rounded-xl border transition-all",
                    newPoll.poll_type === t.id ? cn(theme.border, theme.lightBg, theme.text) : "border-gray-50 bg-gray-50 text-gray-400 hover:border-gray-100"
                  )}
                >
                  <t.icon size={16} />
                  <span className="text-[10px] font-bold uppercase">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <label className="text-xxs font-bold text-gray-400 uppercase tracking-widest mb-1 block">Options</label>
            {newPoll.options.map((option, index) => (
              <div key={index} className="space-y-2">
                <div className="flex gap-2">
                  <input 
                    type="text"
                    value={option.text}
                    onChange={(e) => handleOptionTextChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className={cn(
                      "flex-1 px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-100 outline-none text-sm font-medium transition-all",
                      theme.ring,
                      `focus:${theme.border}`
                    )}
                  />
                  <div className="flex gap-1">
                    <div className="relative">
                      <input 
                        type="file" 
                        id={`live-opt-img-${index}`}
                        onChange={(e) => handleOptionImageChange(index, e)}
                        accept="image/*"
                        className="hidden"
                      />
                      <label 
                        htmlFor={`live-opt-img-${index}`}
                        className={cn(
                          "h-full px-3 rounded-xl flex items-center justify-center cursor-pointer transition-all border",
                          option.image ? cn(theme.bg, "text-white shadow-md", theme.shadow) : "bg-gray-50 text-gray-400 hover:bg-gray-100 border-gray-100"
                        )}
                      >
                        <ImageIcon size={16} />
                      </label>
                    </div>
                    {newPoll.options.length > 2 && (
                      <button onClick={() => removeOption(index)} className="p-2.5 bg-gray-50 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl border border-gray-100 transition-all">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
                {option.image && (
                  <div className={cn("ml-2 relative w-20 h-14 rounded-lg overflow-hidden group border", theme.lightBorder)}>
                    <img src={option.image} alt="" className="w-full h-full object-cover" />
                    <button 
                      onClick={() => {
                        const next = [...newPoll.options];
                        next[index].image = null;
                        setNewPoll(prev => ({ ...prev, options: next }));
                      }}
                      className="absolute top-0.5 right-0.5 p-1 bg-black/40 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={10} />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {newPoll.options.length < 5 && (
              <button 
                onClick={addOption}
                className={cn("flex items-center gap-2 text-xxs font-bold uppercase tracking-widest hover:underline mt-1", theme.text)}
              >
                <Plus size={12} />
                Add Option
              </button>
            )}
          </div>

          {error && <p className="text-xs text-red-500 font-medium">{error}</p>}

          <button 
            onClick={handleCreatePoll}
            disabled={isSlowMode}
            className={cn(
              "w-full py-3 rounded-xl font-bold uppercase tracking-widest text-xs shadow-lg transition-all text-white",
              theme.bg, theme.shadow, theme.hover,
              isSlowMode && "opacity-50 cursor-not-allowed shadow-none"
            )}
          >
            {isSlowMode ? `Wait ${timer}s...` : "Launch Poll"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col h-full bg-white rounded-3xl border shadow-xl overflow-hidden", theme.lightBorder, theme.shadow)}>
      <div className={cn("p-5 border-b flex flex-col gap-4 md:flex-row items-start md:items-center justify-between bg-gradient-to-br from-white", theme.lightBg, theme.lightBorder)}>
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-2xl", theme.lightBg, theme.text)}>
            <BarChart3 size={20} className="opacity-20 fill-current" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 leading-none">Live Polls</h3>
            <div className="mt-1.5">
               <LiveSyncIndicator isSyncing={isSyncing} variant={variant} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isModerator && (
            <button 
              onClick={() => setIsCreating(true)}
              className={cn("text-white px-5 py-2.5 rounded-2xl text-[10px] font-bold uppercase tracking-widest transition-all shadow-lg", theme.bg, theme.shadow, theme.hover)}
            >
              <Plus size={14} className="inline mr-1" />
              New Poll
            </button>
          )}
          <div className={cn(
            "flex items-center gap-1.5 px-3 py-1 rounded-lg border",
            sessionState === 'live' ? "bg-red-50 text-red-600 border-red-100" : "bg-gray-50 text-gray-500 border-gray-100"
          )}>
             <div className={cn("w-1.5 h-1.5 rounded-full animate-pulse", sessionState === 'live' ? "bg-red-500" : "bg-gray-400")} />
             <span className="text-[10px] font-bold uppercase tracking-widest">{sessionState}</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className={cn("p-4 bg-gradient-to-br to-white border-b", theme.lightBg, theme.lightBorder)}>
          <InteractionTip message="Vote to see real-time community results" variant={variant} />
          {!user && polls.length > 0 && (
            <AuthCTA message="Sign in to participate in the polls." variant={variant} />
          )}
          {error && <p className="mt-3 text-xs text-red-500 font-bold">{error}</p>}
        </div>

        <div className="flex-1 overflow-y-auto space-y-10 p-6 scrollbar-thin">
        {polls.length > 0 ? (
          [...polls].sort((a, b) => {
            if (a.highlighted && !b.highlighted) return -1;
            if (!a.highlighted && b.highlighted) return 1;
            return new Date(b.created_at || Date.now()) - new Date(a.created_at || Date.now());
          }).map((poll) => {
            const totalVotes = poll.options?.reduce((sum, option) => sum + option.votes, 0) || 0;
            const selectedOption = selectedOptions[poll.id];

            return (
              <div key={poll.id} className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-2 mb-2">
                      <h3 className="font-bold text-gray-900 text-sm sm:text-base md:text-lg break-words leading-tight">{poll.question}</h3>
                      {poll.highlighted && <Pin size={14} className={cn("fill-current shrink-0 mt-0.5", theme.text)} />}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <div className={cn("w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl p-0.5 border overflow-hidden shadow-sm shrink-0", theme.lightBg, theme.lightBorder)}>
                          <img src={poll.author_user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${poll.author_user?.name || poll.author || 'guest'}`} alt="avatar" className="w-full h-full object-cover rounded-lg sm:rounded-xl" />
                        </div>
                        <span className="text-[10px] sm:text-xs font-bold text-gray-700 truncate max-w-[100px] sm:max-w-[150px]">{poll.author_user?.name || poll.author || 'Guest'}</span>
                        {(String(poll.author_id) === String(roomData?.owner_id) || String(poll.author_id) === String(roomData?.host_id)) && (
                          <span className={cn("text-[7px] sm:text-[8px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-widest", theme.text, theme.lightBg, theme.lightBorder)}>Host</span>
                        )}
                      </div>
                      {poll.poll_type !== 'single' && (
                        <span className={cn("text-[7px] sm:text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-widest", theme.text, theme.lightBg, theme.lightBorder)}>
                          {poll.poll_type === 'multiple' ? 'Multiple Choice' : 'Ranked Choice'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 ml-auto sm:ml-0">
                    {isModerator && (
                      <button 
                        onClick={() => togglePin(poll.id, poll.highlighted)}
                        className={cn("p-1.5 rounded-lg transition-colors", poll.highlighted ? cn(theme.text, theme.lightBg) : "text-gray-400 hover:bg-gray-100")}
                        title={poll.highlighted ? "Unpin" : "Pin to Top"}
                      >
                        <Pin size={14} fill={poll.highlighted ? "currentColor" : "none"} />
                      </button>
                    )}
                    {isModerator && poll.status === 'active' && (
                      <button 
                        onClick={() => handleEndPoll(poll.id)}
                        className="text-orange-500 font-bold uppercase tracking-widest text-[10px] hover:underline shrink-0"
                      >
                        End Poll
                      </button>
                    )}
                    {(isModerator || String(poll.author_id) === String(user?.id)) && (
                      <button 
                        onClick={() => handleDeletePoll(poll.id)}
                        className="text-red-500 font-bold uppercase tracking-widest text-[10px] hover:underline shrink-0"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {poll.options?.map((option) => {
                    const percentage = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
                    const isSelected = poll.poll_type === 'multiple' 
                      ? (Array.isArray(selectedOption) && selectedOption.includes(option.id))
                      : selectedOption === option.id;

                    const canVote = poll.poll_type === 'multiple'
                      ? !isSelected
                      : !selectedOption;

                    return (
                      <button
                        key={option.id}
                        disabled={sessionState !== 'live' || !user || !canVote || poll.status !== 'active'}
                        onClick={() => handleVote(poll.id, option.id, poll.poll_type)}
                        className={cn(
                          "w-full p-3 sm:p-4 rounded-xl sm:rounded-2xl border transition-all relative overflow-hidden group text-left",
                          isSelected ? cn(theme.border, "bg-white shadow-md", theme.shadow) : "border-gray-100 bg-gray-50/50 hover:border-gray-200",
                          poll.status !== 'active' && "opacity-80"
                        )}
                      >
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          className={cn(
                            "absolute inset-0 opacity-10 transition-colors",
                            option.color || theme.bg
                          )}
                        />
                        
                        <div className="flex gap-3 sm:gap-4 relative z-10">
                          {option.image && (
                            <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-lg sm:rounded-xl overflow-hidden shrink-0 border border-gray-100 shadow-sm">
                              <img src={option.image} alt="" className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div className="flex-1 flex justify-between items-center min-w-0">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                               <div className={cn(
                                  "w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center transition-all shrink-0",
                                  isSelected ? cn(theme.border, theme.bg, "text-white") : "border-gray-200 bg-white"
                               )}>
                                  {isSelected && <CheckCircle2 size={12} className="sm:w-3.5 sm:h-3.5" />}
                               </div>
                               <span className={cn("font-bold text-sm sm:text-base break-words whitespace-normal leading-tight", isSelected ? theme.text : "text-gray-700")}>{option.text}</span>
                            </div>
                            {(selectedOption || sessionState !== 'live' || isModerator || poll.status !== 'active') && (
                              <div className="text-right ml-2 sm:ml-4 shrink-0">
                                 <p className={cn("text-base sm:text-lg font-bold leading-none", isSelected ? theme.text : "text-gray-900")}>{percentage}%</p>
                                 <p className="text-[8px] sm:text-xxs font-bold text-gray-400 uppercase tracking-widest mt-1">{option.votes} {option.votes === 1 ? 'vote' : 'votes'}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })
        ) : (
          <EmptyState 
            icon={BarChart3}
            title="No active polls available"
            description="Be the first to create a poll and gather community feedback!"
            variant={variant}
            action={isModerator && (
              <button 
                onClick={() => setIsCreating(true)}
                className={cn("text-white px-6 py-3 rounded-2xl font-bold uppercase tracking-widest text-[10px] shadow-lg", theme.bg, theme.shadow, theme.hover)}
              >
                Create First Poll
              </button>
            )}
          />
        )}
      </div>
    </div>

    <ConfirmModal 
      {...confirmConfig}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
      />
    </div>
  );
};

export default LivePolls;
